"use client";

import { scopeProjectRef, scopeThreadRef } from "@t3tools/client-runtime";
import { useNavigate } from "@tanstack/react-router";
import { slugifyProjectName } from "@t3tools/shared/projectSlug";
import type { ClientOperatorDefaults } from "@t3tools/contracts";
import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { readEnvironmentApi } from "../environmentApi";
import { usePrimaryEnvironmentId } from "../environments/primary";
import { useHandleNewThread } from "../hooks/useHandleNewThread";
import { useSettings } from "../hooks/useSettings";
import { findProjectByPath, inferProjectTitleFromPath } from "../lib/projectPaths";
import { allocateUniqueGuidedProjectSlug, joinGuidedProjectPath } from "../lib/guidedProjectRoot";
import { readLocalApi } from "../localApi";
import { getLatestThreadForProject } from "../lib/threadSort";
import { newCommandId, newProjectId } from "../lib/utils";
import { getServerConfig, useOperatorDefaults, usePolicyControl } from "../rpc/serverState";
import { useGuidedProjectStore } from "../guidedProjectStore";
import {
  selectProjectsAcrossEnvironments,
  selectSidebarThreadsAcrossEnvironments,
  useStore,
} from "../store";
import { buildThreadRouteParams } from "../threadRoutes";
import { PolicyControlTooltip } from "./PolicyControlTooltip";
import { describePolicyControl } from "../actionPolicy";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { stackedThreadToast, toastManager } from "./ui/toast";
import { useTranslation } from "react-i18next";

export function GuidedProjectDialog() {
  const open = useGuidedProjectStore((state) => state.open);
  const setOpen = useGuidedProjectStore((state) => state.setOpen);
  const operatorDefaults = useOperatorDefaults();

  if (operatorDefaults === null) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPopup className="max-w-md" showCloseButton={false}>
        {open ? (
          <GuidedProjectDialogBody
            operatorDefaults={operatorDefaults}
            onClose={() => setOpen(false)}
          />
        ) : null}
      </DialogPopup>
    </Dialog>
  );
}

function GuidedProjectDialogBody({
  operatorDefaults,
  onClose,
}: {
  readonly operatorDefaults: ClientOperatorDefaults;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const settings = useSettings();
  const projectControl = usePolicyControl("project.create");
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const projects = useStore(useShallow(selectProjectsAcrossEnvironments));
  const threads = useStore(useShallow(selectSidebarThreadsAcrossEnvironments));
  const { handleNewThread } = useHandleNewThread();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const environmentId = primaryEnvironmentId;

  const trimmedName = name.trim();
  const canSubmit =
    !projectControl.disabled && trimmedName.length > 0 && environmentId !== null && !submitting;
  const canImportFolder =
    environmentId !== null && typeof window !== "undefined" && window.desktopBridge !== undefined;

  async function addProjectAt(input: {
    readonly workspaceRoot: string;
    readonly title: string;
    readonly createWorkspaceRootIfMissing: boolean;
    /**
     * Shown instead of the failure's own text. Workspace normalization answers
     * in English — «Workspace root does not exist: …» — and the lawyer reading
     * this toast reads Russian.
     */
    readonly failureDescription?: string;
  }): Promise<void> {
    const { workspaceRoot, title, createWorkspaceRootIfMissing } = input;
    if (
      !environmentId ||
      describePolicyControl(getServerConfig()?.capabilities["project.create"]).disabled
    )
      return;
    const api = readEnvironmentApi(environmentId);
    if (!api) return;

    const environmentProjects = projects.filter(
      (project) => project.environmentId === environmentId,
    );

    setSubmitting(true);
    try {
      const existing = findProjectByPath(environmentProjects, workspaceRoot);
      if (existing) {
        const latestThread = getLatestThreadForProject(
          threads.filter((thread) => thread.environmentId === existing.environmentId),
          existing.id,
          settings.sidebarThreadSortOrder,
        );
        if (latestThread) {
          await navigate({
            to: "/$environmentId/$threadId",
            params: buildThreadRouteParams(
              scopeThreadRef(latestThread.environmentId, latestThread.id),
            ),
          });
        } else {
          await handleNewThread(scopeProjectRef(existing.environmentId, existing.id)).catch(
            () => undefined,
          );
        }
        onClose();
        return;
      }

      const projectId = newProjectId();
      await api.orchestration.dispatchCommand({
        type: "project.create",
        commandId: newCommandId(),
        projectId,
        title,
        workspaceRoot,
        createWorkspaceRootIfMissing,
      });
      await handleNewThread(scopeProjectRef(environmentId, projectId)).catch(() => undefined);
      onClose();
    } catch (error) {
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: t("common/failedToAddProject_7upqjv"),
          description:
            input.failureDescription ??
            (error instanceof Error ? error.message : t("common/anErrorOccurred_gd0s0s")),
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function submit(): Promise<void> {
    if (!environmentId || !canSubmit) return;

    const root = operatorDefaults.addProjectBaseDirectory;
    const baseSlug = slugifyProjectName(trimmedName);
    const environmentProjects = projects.filter(
      (project) => project.environmentId === environmentId,
    );
    const uniqueSlug = allocateUniqueGuidedProjectSlug(baseSlug, root, environmentProjects);

    await addProjectAt({
      workspaceRoot: joinGuidedProjectPath(root, uniqueSlug),
      title: trimmedName,
      createWorkspaceRootIfMissing: true,
    });
  }

  async function importFolder(): Promise<void> {
    if (!environmentId || submitting || projectControl.disabled) return;
    const localApi = readLocalApi();
    if (!localApi) return;

    let picked: string | null = null;
    try {
      picked = await localApi.dialogs.pickFolder();
    } catch {
      // Picker failures leave the dialog open, matching the palette flow.
      return;
    }
    if (!picked) return;

    await addProjectAt({
      workspaceRoot: picked,
      title: inferProjectTitleFromPath(picked) || picked,
      createWorkspaceRootIfMissing: false,
      failureDescription: t("common/checkThatTheSelected_183b7o"),
    });
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <DialogHeader>
        <DialogTitle>{t("common/newProject_tl3wc2")}</DialogTitle>
      </DialogHeader>
      <div className="px-6 pb-2">
        <Input
          ref={inputRef}
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t("common/projectName_kjeego")}
          aria-label={t("common/projectName_kjeego")}
          disabled={submitting}
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      {canImportFolder ? (
        <>
          <div className="flex items-center gap-3 px-6 py-1 text-muted-foreground text-xs">
            <div className="h-px flex-1 bg-border" />
            {t("common/or_v1l54n")}
            <div className="h-px flex-1 bg-border" />
          </div>
          <div className="px-6 pb-2">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={submitting || projectControl.disabled}
              data-testid="guided-project-import-trigger"
              onClick={() => void importFolder()}
            >
              {t("common/chooseAFolderOn_eakxkh")}
            </Button>
          </div>
        </>
      ) : null}
      {projectControl.reason ? (
        <p className="px-6 pb-2 text-sm text-muted-foreground">{t(projectControl.reason)}</p>
      ) : null}
      <DialogFooter>
        <DialogClose render={<Button type="button" variant="outline" disabled={submitting} />}>
          {t("common/cancel_8mt5fw")}
        </DialogClose>
        <PolicyControlTooltip reason={projectControl.reason}>
          <Button type="submit" disabled={!canSubmit}>
            {submitting ? t("common/adding_du50w8") : t("common/addProject_xda4s6")}
          </Button>
        </PolicyControlTooltip>
      </DialogFooter>
    </form>
  );
}
